import { useState, useCallback, useEffect } from "react";
import { useFirebase } from "../../wrappers/firebase";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  documentId,
  orderBy,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { useParams } from "react-router";
import chunkArray from "../../helpers/chunkArray";
import useUserStore from "../../store/userStore";
import { shallow } from "zustand/shallow";

const useProfile = () => {
  const { getData, updateDocument, addDocument, deleteDocument } =
    useFirebase();
  const queryClient = useQueryClient();
  const profiles = queryClient.getQueryData(["profiles"]) ?? [];
  const { username: stateUsername, uid: stateUserId } = useUserStore(
    (state) => state.user,
    shallow
  );
  const setUser = useUserStore((state) => state.setUser);

  const [isFetching, setIsFetching] = useState();

  // reactive function to get a profile by uid or username
  async function getProfile({ uid, username }) {
    setIsFetching(true);
    try {
      let _cachedProfile = null;

      if (!uid && !username) return; // Return early

      if (uid) {
        const _profilesQuery = queryClient.getQueriesData({
          queryKey: ["profiles"],
        });
        const allProfiles = _profilesQuery
          ?.map(([, data]) => data)
          .flat()
          .filter(
            (item) => item && typeof item === "object" && !Array.isArray(item)
          );
        _cachedProfile = allProfiles?.find(({ id }) => id === uid);
      } else {
        _cachedProfile = queryClient.getQueryData(["profiles", username]);
      }

      if (_cachedProfile) {
        setIsFetching(false);
        return _cachedProfile;
      }

      const _dbData = await getData({
        path: uid ? `profiles/${uid}` : "profiles",
        filters: uid ? null : [where("username", "==", username)],
        limit: 1,
      });
      const profile = _dbData?.docs?.[0] ?? _dbData;

      const isFollowedByUser = await getData({
        path: `profiles/${profile.id}/followers/${stateUserId}`,
        checkExistence: true,
      });
      profile.isFollowedByUser = isFollowedByUser;

      const postCount = await getData({
        path: `profiles/${profile.id}/posts`,
        filters: [where("isDeleted", "==", false)],
        limit: null,
        getOnlyLength: true,
      });
      profile.postCount = postCount;

      if (profile) {
        queryClient.setQueryData(["profiles", profile.username], (oldData) => ({
          ...oldData,
          ...profile,
        }));
      }

      // fetch if not in cache
      setIsFetching(false);
      return profile;
    } catch (ex) {
      console.error(`Error fetching profile doc: ${uid ?? username}`, ex);
    }
  }

  async function getMultipleProfiles({ uids }) {
    setIsFetching(true);
    const batchedUsers = chunkArray(uids, 10);
    const profiles = (
      await Promise.all(
        batchedUsers.map((uidBatch) =>
          Promise.all(uidBatch.map(async (uid) => await getProfile({ uid })))
        )
      )
    )?.flat(Infinity);

    setIsFetching(false);
    return profiles;
  }

  const { mutateAsync: getAllProfiles, isPending: isAllProfilesFetching } =
    useMutation({
      mutationFn: async () => {
        const results = await getData({ path: `profiles` });
        return results;
      },
      onError: (ex) => {},
      onSuccess: (data) => {
        if (!data) return;
        const docs = data.docs ?? data;
        if (Array.isArray(docs)) {
          queryClient.setQueryData(["profiles"], (old) => {
            const existing = old ?? [];
            const merged = [...existing];
            for (const doc of docs) {
              const idx = merged.findIndex((p) => p.id === doc.id);
              if (idx >= 0) {
                merged[idx] = { ...merged[idx], ...doc };
              } else {
                merged.push(doc);
              }
            }
            return merged;
          });
        } else {
          queryClient.setQueryData(["profiles"], (old) => [
            ...(old ?? []),
            docs,
          ]);
        }
      },
      retry: 2,
    });

  function updateProfile({ uid, data }) {
    let _oldData = {};
    queryClient.setQueryData(["profiles", stateUsername], (oldData) => {
      _oldData = oldData;
      return {
        ...oldData,
        ...data,
      };
    });

    if (uid === stateUserId) {
      setUser(data);
    }

    (async () => {
      try {
        await updateDocument({
          path: `profiles/${uid}`,
          data: data,
        });
      } catch (ex) {
        queryClient.setQueryData(["profiles", stateUsername], () => {
          return _oldData;
        });
      }
    })();

    return data;
  }

  function followProfile({ username }) {
    (async () => {
      const { id: userId } = await getProfile({ username });

      await addDocument({
        path: `profiles/${userId}/followers/${stateUserId}`,
        data: {
          createdAt: serverTimestamp(),
        },
      });
      await addDocument({
        path: `profiles/${stateUserId}/following/${userId}`,
        data: {
          createdAt: serverTimestamp(),
        },
      });
    })();

    queryClient.setQueryData(["profiles", username], (old) => ({
      ...old,
      isFollowedByUser: true,
    }));
  }

  function unfollowProfile({ username }) {
    (async () => {
      const { id: userId } = await getProfile({ username });

      await deleteDocument(`profiles/${userId}/followers/${stateUserId}`);
      await deleteDocument(`profiles/${stateUserId}/following/${userId}`);
    })();

    queryClient.setQueryData(["profiles", username], (old) => ({
      ...old,
      isFollowedByUser: false,
    }));
  }

  async function getSuggestedProfiles(limit = 4) {
    setIsFetching(true);
    const { docs: profiles } = await getData({
      path: "profiles",
      filters: [orderBy("streamCount", "desc")],
      limit,
    });

    const _updatedProfiles = [];
    await Promise.all(
      profiles?.map(async (profile) => {
        const isFollowedByUser = await getData({
          path: `profiles/${profile.id}/followers/${stateUserId}`,
          checkExistence: true,
        });
        _updatedProfiles.push({ ...profile, isFollowedByUser });

        queryClient.setQueryData(["profiles", profile.username], (oldData) => ({
          ...oldData,
          ...profile,
        }));
      })
    );

    // fetch if not in cache
    setIsFetching(false);
    return _updatedProfiles;
  }

  // const { mutateAsync: updateProfile, isPending: isProfileUpdating } =
  //   useMutation({
  //     mutationFn: async ({ uid, data }) => {
  //       const result = await updateDocument({
  //         path: `profiles/${uid}`,
  //         data: data,
  //       });
  //       return result;
  //     },
  //     onError: (ex) => {},
  //     onSuccess: (data) => {
  //       if (data) {
  //         queryClient.setQueryData(["profiles"], (old) => {
  //           if (!old) return [data];
  //           return old.map((profile) =>
  //             profile.id === data.id ? { ...profile, ...data } : profile
  //           );
  //         });
  //       }
  //       return data;
  //     },
  //     retry: 2,
  //   });

  return {
    getProfile,
    followProfile,
    unfollowProfile,
    profiles,
    getAllProfiles,
    updateProfile,
    isFetching,
    // isProfileUpdating,
    isAllProfilesFetching,
    getMultipleProfiles,
    getSuggestedProfiles,
  };
};

export default useProfile;
